import { useSyncExternalStore } from "react";

// In-memory log of the last few calls, shown in the "Under the hood" panel.
//
// Entries are presented as the direct VX integration a customer would build:
// the demo backend forwards /demo/vx/* bodies to VX (adding the API key and
// dropping the demo-only keys) and returns VX's response, so the view here is
// derived from the very object we send, never typed by hand. The bearer token
// for the demo backend and the real API key are never stored.

const MAX_ENTRIES = 5;
const VX_BASE = "https://vx.dcams.app/api/journey";
const VX_KEY_HEADER = { "x-api-key": "<your VX API key>" };
const DEMO_ONLY_KEYS = ["demo", "method", "test_mode"];

let entries = [];
let lastRequestId = null;
let modalOpen = false;
const listeners = new Set();
const modalListeners = new Set();

function emit() {
  listeners.forEach((fn) => fn());
}

export function isVxPath(path) {
  return typeof path === "string" && path.startsWith("/demo/vx/");
}

function stripDemoKeys(payload) {
  const out = {};
  Object.keys(payload || {}).forEach((k) => {
    if (!DEMO_ONLY_KEYS.includes(k)) out[k] = payload[k];
  });
  return out;
}

// Build the "what VX receives" view of a call from the same payload we send.
export function vxView(path, payload, meta = {}) {
  if (path === "/demo/vx/create-journey.php") {
    return {
      kind: "create",
      label: "Create journey",
      method: "POST",
      url: `${VX_BASE}/create_journey`,
      headers: { ...VX_KEY_HEADER, "Content-Type": "application/json" },
      body: stripDemoKeys(payload),
    };
  }
  if (path === "/demo/vx/get-result.php") {
    const id = payload?.request_id || lastRequestId || "{request_id}";
    return {
      kind: "status",
      label: meta.attempt ? `Status check (attempt ${meta.attempt})` : "Status check",
      method: "GET",
      url: `${VX_BASE}/get_result/${id}`,
      headers: { ...VX_KEY_HEADER },
      body: null,
    };
  }
  // Older demo-backend endpoints: keep them, but never show a host.
  const base = path.replace(/\.php$/, "");
  return {
    kind: "backend",
    label: base.split("/").pop(),
    method: "POST",
    url: base,
    headers: { "Content-Type": "application/json" },
    body: payload,
  };
}

// VX's own body, status and ok flag from what the demo backend returned.
export function vxResponseView(path, httpStatus, data, ok) {
  if (isVxPath(path) && data && typeof data === "object" && "body" in data) {
    const status = data.http_code ?? httpStatus;
    return { status, response: data.body, ok: ok && (!data.http_code || (data.http_code >= 200 && data.http_code < 300)) };
  }
  return { status: httpStatus, response: data, ok };
}

export function startApiLog({ path, payload, meta }) {
  const view = vxView(path, payload, meta);
  const entry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    at: new Date().toISOString(),
    path,
    ...view,
    ...(meta || {}),
    status: null,
    ok: null,
    response: null,
    error: null,
    durationMs: null,
    startedAt: Date.now(),
  };
  entries = [entry, ...entries].slice(0, MAX_ENTRIES);
  emit();
  return entry.id;
}

export function finishApiLog(id, patch) {
  entries = entries.map((e) =>
    e.id === id ? { ...e, ...patch, durationMs: Date.now() - e.startedAt } : e
  );
  emit();
}

export function rememberRequestId(data) {
  if (data && typeof data === "object" && data.request_id) {
    lastRequestId = String(data.request_id);
  }
}

export function getLastRequestId() {
  return lastRequestId;
}

export function clearApiLog() {
  entries = [];
  emit();
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useApiLog() {
  return useSyncExternalStore(subscribe, () => entries, () => entries);
}

// Whether the VX verification modal is on screen, so the panel can sit above it.
export function setVerificationModalOpen(open) {
  if (modalOpen === !!open) return;
  modalOpen = !!open;
  modalListeners.forEach((fn) => fn());
}

function subscribeModal(fn) {
  modalListeners.add(fn);
  return () => modalListeners.delete(fn);
}

export function useVerificationModalOpen() {
  return useSyncExternalStore(subscribeModal, () => modalOpen, () => modalOpen);
}
